/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-non-null-assertion */
import AtomicSDK, {
  AACHeadlessStreamContainer,
  HorizontalContainerConfig,
} from '@atomic.io/action-cards-web-sdk';
import React, { useCallback, useEffect } from 'react';
import { useAtomic } from './AtomicProvider';
import { selectConfigByKey } from '../firebase/remote-config/remoteConfig.slice';
import { useSelector } from 'react-redux';
import AtomicEmbedHorizontal from './AtomicEmbedHorizontal';
import { Box, Stack, useMediaQuery, useTheme } from '@mui/material';

const initialCardCount = { cardCount: 0, totalCards: 0 };

export const ATOMIC_STREAM_ID_AGREEMENTS =
  process.env.REACT_APP_ATOMIC_STREAM_CONTAINER_ID_AGREEMENTS!;

export const AgreementsCampaignsAtomic = () => {
  const atomicEnabled = useSelector(selectConfigByKey('atomicEnabled'));
  const atomicAgreementsEnabled = useSelector(selectConfigByKey('atomicPromotionsEnabled'));
  const atomic = useAtomic();
  const [{ totalCards }, setAgreementCardCount] = React.useState<{
    cardCount: number;
    totalCards: number;
  }>(initialCardCount);
  const agreementStream = React.useRef<AACHeadlessStreamContainer>();

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));

  const updateCardCount = useCallback(() => {
    AtomicSDK.requestUserMetrics()
      .then((metrics) => {
        const cardCount = metrics.unseenCardsForStreamContainer(ATOMIC_STREAM_ID_AGREEMENTS);
        const totalCards = metrics.totalCardsForStreamContainer(ATOMIC_STREAM_ID_AGREEMENTS);
        setAgreementCardCount({ cardCount, totalCards });
      })
      .catch((err) => {
        if (err?.toString().includes('Device is not connected to the internet')) {
          console.log(err);
        } else {
          console.error(err);
        }
      });
  }, [setAgreementCardCount]);

  useEffect(() => {
    if (atomic.atomicReady) {
      updateCardCount();
      agreementStream.current = AtomicSDK.observableStreamContainer({
        streamContainerId: ATOMIC_STREAM_ID_AGREEMENTS,
        cardListRefreshInterval: 10,
        features: {
          runtimeVariableAnalytics: false,
        },
        onCardCountChanged() {
          updateCardCount();
          return null;
        },
        cardFeedHandler() {
          return null;
        },
      });
    }
  }, [atomic.atomicReady, updateCardCount]);

  const configMemo = React.useMemo(() => {
    return {
      enabled: true,
      cardWidth: isSm ? (window.innerWidth / 100) * 60 : 354,
      emptyStyle: 'standard',
      headerAlignment: 'left',
      scrollMode: 'snap',
      lastCardAlignment: 'left',
    } as HorizontalContainerConfig;
  }, [isSm]);

  if (atomicAgreementsEnabled && atomicEnabled && atomic.atomicReady) {
    return (
      <Stack
        width='100%'
        sx={{
          ...(totalCards <= 0 ? { display: 'none' } : { display: 'flex' }),
        }}
      >
        <Box
          sx={{
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            gap: '16px',
          }}
        >
          <AtomicEmbedHorizontal
            onChange={({ cardCount, totalCards }) => {
              setAgreementCardCount({ cardCount, totalCards });
            }}
            containerConfig={configMemo}
            containerId={ATOMIC_STREAM_ID_AGREEMENTS}
          />
        </Box>
      </Stack>
    );
  }
};
